<template>
  <div class="page-block">
    <a-alert
      v-if="isPublishable && !componentMeta"
      class="page-block__error"
      :message="$t(`page.error.meta`, { type: entityType })"
      type="error"
      showIcon
    />

    <a-spin
      v-else
      :spinning="(!data && !requestError) || !config || loadingQueries > 0"
    >
      <div class="spin-wrap">
        <template v-if="fieldsMeta && config">
          <div class="page-block__title">
            <a-icon
              v-if="showSidebarButton"
              class="menu-toggle"
              type="menu"
              @click="toggleMenu"
            />
            <h1 class="ellipsis">{{ title }}</h1>
            <a-icon
              v-if="canEditModel"
              class="page-block__model-link"
              type="edit"
              @click="editModel"
            />
          </div>

          <the-filter
            v-if="filterParams"
            :pageConfig="config"
            :formConfig="formConfig"
            :tableConfig="tableConfig"
            :filters="filterParams"
            :configData="cachedConfig"
            :configLayout="CONFIG_LAYOUT"
            :fieldsMeta="fieldsMeta"
            :entityType="entityType"
            :canCreateModel="canCreateModel"
            isPublishable
            @setFilterParams="setFilterParams"
            @createEntity="entityClick"
            @configUpdated="saveConfig"
            @pageSizeUpdated="updatePageSize"
          >
            <template
              v-if="data"
              #pagination
            >
              <template v-if="switchButton">
                <template v-for="(button, index) of switchMeta.fields">
                  <a-button
                    v-if="switchButtonIndex !== index"
                    :key="index"
                    type="primary"
                    @click="setSwitchButton(index)"
                  >
                    {{ button.label }}
                  </a-button>
                </template>
              </template>

              <a-pagination
                v-if="!data.noPagination"
                v-model="page"
                simple
                hideOnSinglePage
                :disabled="loadingQueries > 0"
                :total="data.totalCount"
                :pageSize="config.pageSize"
              />
            </template>

            <template
              v-if="showImportButton"
              #last
            >
              <file-import :entityType="entityType" />
            </template>
          </the-filter>

          <component
            :is="rendererComponent"
            v-if="data"
            :entityType="entityType"
            :entityFields="fieldsMeta"
            :defaultSort="sortParams"
            :data="data"
            :config="config"
            :formConfig="formConfig"
            :tableConfig="tableConfig"
            :canReadModel="canReadModel"
            :canUpdateModel="canUpdateModel"
            @openInModal="(path) => $emit('openInModal', path)"
            @updateEntity="updateEntity"
            @setSortParams="setSortParams"
            @entityClick="entityClick"
            @updateRows="updateRows"
          />

          <a-alert
            v-else-if="requestError"
            type="error"
            :message="requestError"
          />
        </template>
      </div>
    </a-spin>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import store from '@/store';
import {
  bus,
  deepClone,
  storage,
  XHR,
  parseJson,
  uniqueId,
  composeGqlQueryFromActionWithVariables,
} from '@/helpers';
import { GqlObject } from '@/helpers/GqlObject';
import EntityService from '@/services/EntityService';
import FormConfigService from '@/services/FormConfigService';
import TableConfigService from '@/services/TableConfigService';
import TheFilter from '@/components/page/filter/TheFilter.vue';
import TheGantt from '@/components/page/gantt/TheGantt.vue';
import TheKanban from '@/components/page/kanban/TheKanban.vue';
import TheTable from '@/components/page/table/TheTable.vue';
import TheTiles from '@/components/page/tiles/TheTiles.vue';
import TheCalendar from '@/components/page/calendar/TheCalendar.vue';
import TheMap from '@/components/page/map/TheMap.vue';
import FormsList from '@/components/page/forms-list/FormsList.vue';
import SoCourseList from '@/components/custom-entities/SoCourseList/SoCourseList.vue';
import SoPromocodes from '@/components/custom-entities/SoPromocodes/SoPromocodes.vue';
import AuroReleases from '@/components/custom-entities/AuroReleases/AuroReleases.vue';
import { CONFIG_LAYOUT, prepareConfig } from '@/components/page/config/configFormLayout.js';
import { getFilterRendererByFormRenderer } from '@/components/page/filter/fieldOperators.js';
import FileImport from './components/FileImport.vue';

const DATA_REQUEST_DEBOUNCE = 900;

export default {
  components: {
    TheFilter,
    TheGantt,
    TheKanban,
    TheTable,
    TheInlineTable: TheTable,
    TheTiles,
    TheCalendar,
    TheMap,
    FileImport,
    FormsList,
    SoCourseList,
    SoPromocodes,
    AuroReleases,
  },

  props: {
    pageUrl: {
      type: String,
      required: true,
    },
    entityType: {
      type: String,
      required: true,
    },
    showSidebarButton: {
      type: Boolean,
      required: true,
    },
    isUseConfig: {
      type: Boolean,
      default: true,
    },
    setupFilters: {
      type: Object,
      default: () => ({}),
    },
    setupSortParams: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      loadingQueries: 0,
      CONFIG_LAYOUT,
      page: Number(this.$route.query.page) || 1,
      filterParams: null,
      sortParams: [],
      cachedConfig: null,
      requestError: null,
      queryPageSize: Number(this.$route.query.pageSize),
      localStoredTableConfig: (storage.get('viewConfigData') || {})[this.pageUrl],
      configData: null,

      data: null,
      switchMeta: null,
      switchButtonIndex: null,
      meta: null,
      listMeta: null,
    };
  },

  computed: {
    componentMeta() {
      return store.state.meta.components[this.entityType];
    },
    user() {
      return store.state.user;
    },
    canEditModel() {
      return (
        this.user.isConstructor &&
        !!store.state.meta.entities.find((e) => e.name === this.entityType)
      );
    },
    customForms() {
      return (
        store.state.activeSidebarItem.customprops?.form?.actions?.map((form) =>
          typeof form === 'string' ? { action: form } : form,
        ) || []
      );
    },
    desktop() {
      return store.state.isDesktop;
    },
    config() {
      return this.cachedConfig?.config;
    },
    formConfig() {
      if (this.isPublishable) {
        return FormConfigService.getFormConfig(this.entityType);
      }

      return (
        this.meta &&
        FormConfigService.getPublishableFormConfig(store.state.activeSidebarItem.id, this.meta)
      );
    },
    tableConfig() {
      return TableConfigService.getTableConfig(this.entityType);
    },
    title() {
      return (
        this.formConfig.locale[store.state.lang].entity ||
        store.state.activeSidebarItem.title ||
        this.entityType
      );
    },
    rendererComponent() {
      const customComponent = store.state.activeSidebarItem.customprops?.customComponent;
      if (customComponent) return customComponent;
      if (this.customForms.length) return 'forms-list';
      return `the-${this.config.displayType.value}`;
    },
    isTable() {
      return this.config.displayType.value === 'table';
    },
    operations() {
      return !this.isPublishable
        ? {
            CREATE: true,
            READ: true,
            UPDATE: true,
          }
        : this.componentMeta?.operations || {};
    },
    canCreateModel() {
      return this.operations.CREATE && !this.customForms.length;
    },
    canReadModel() {
      return this.operations.READ;
    },
    canUpdateModel() {
      return this.operations.UPDATE;
    },

    isPublishable() {
      return !store.state.activeSidebarItem.customprops?.meta;
    },

    dataActions() {
      let sourcesKeys = this.listMeta.fields.map(
        (field) => field.source?.match(/^\$([a-zA-Z_]+?)\./)[1] || 'ctx',
      );

      sourcesKeys = [...new Set(sourcesKeys)].filter((key) => key !== 'ctx');
      return sourcesKeys.map((key) => this.listMeta[key]);
    },

    switchButton() {
      return this.switchMeta?.fields[this.switchButtonIndex];
    },

    fieldsMeta() {
      if (this.isPublishable) {
        return this.listMeta?.fields;
      }

      return this.meta?.fields;
    },

    limit() {
      return this.config.pageSize;
    },

    offset() {
      return this.config.pageSize * (this.page - 1);
    },

    order() {
      return new GqlObject(
        this.sortParams,
        (object) => `{field: "${object.field}", direction: ${object.direction}}`,
      );
    },

    where() {
      if (!this.filterParams?.length) return {};

      const filters = this.filterParams.map(({ field, operator, value }) => ({
        _or: value.map((v) => ({ [field]: { [operator]: v } })),
      }));

      return `{ "_and": ${JSON.stringify(filters)} }`;
    },

    showImportButton() {
      return store.state.meta.entities.find((e) => e.name === this.entityType);
    },
  },

  watch: {
    $route(to, from) {
      if (to.path === from.path && to.fullPath !== from.fullPath) {
        this.filterParams = null;
        this.sortParams = [];
        this.$nextTick(() => {
          this.sortParams = this.getInitialSortParams();
          this.filterParams = this.getInitialFilterParams();
        });
      }
    },
    page() {
      this.updatePageQueryParams();
    },
    formConfig: {
      deep: true,
      handler() {
        this.resetConfig();
      },
    },
  },

  async created() {
    this.configData = this.getBaseConfig();
    this.createRefetchDataMethod();
    bus.$on('refetchTable', this.refetchData);

    this.loadingQueries++;
    await this.loadMeta();
    await this.loadSwitchMeta();
    await this.resetConfig();

    this.sortParams = this.getInitialSortParams();
    this.filterParams = this.getInitialFilterParams();

    this.setSwitchButton(0);
    this.loadingQueries--;
  },

  destroyed() {
    bus.$off('refetchTable', this.refetchData);
  },

  methods: {
    async loadMeta() {
      if (!this.isPublishable) {
        const metaUrl = store.state.activeSidebarItem.customprops?.meta;
        const meta = await XHR.get(metaUrl);
        this.meta = parseJson(meta);

        const url = this.meta.tableBarField.searchButton.action;

        const response = await XHR.get(url, { absoluteUrl: true });
        this.listMeta = parseJson(response);
        return;
      }
      const listMetaUrl = this.isUseConfig
        ? store.state.activeSidebarItem.customprops?.listMeta
        : store.state.flatPagesList.find((el) => el.url === this.pageUrl).customprops?.listMeta;
      const response = await XHR.get(listMetaUrl);
      this.listMeta = parseJson(response);
    },

    async loadSwitchMeta() {
      const url = store.state.activeSidebarItem.customprops?.switchMeta;
      if (!url) return;
      const response = await XHR.get(url);
      this.switchMeta = parseJson(response);
    },

    async setSwitchButton(index) {
      this.switchButtonIndex = index;
      await this.loadData();
    },

    createRefetchDataMethod() {
      this.refetchData = debounce(() => {
        this.loadData();
      }, DATA_REQUEST_DEBOUNCE);
    },

    async loadData() {
      this.loadingQueries++;
      const loadedData = await Promise.all(
        this.dataActions.map((action) => this.loadDataSource(action)),
      );

      const combinedData = loadedData.reduce(
        (acc, data) => {
          acc.totalCount += data.totalCount;
          acc.totalPages = Math.max(acc.totalPages, data.totalPages);
          acc.hasMore = data.hasMore || data.hasMore;
          acc.rows = acc.rows.concat(data.rows);

          acc.noPagination = acc.noPagination || data.noPagination;

          Object.assign(acc, data);
          return acc;
        },
        {
          totalCount: 0,
          totalPages: 0,
          hasMore: false,
          rows: [],
          noPagination: false,
        },
      );

      this.data = combinedData;
      this.loadingQueries--;
    },

    async loadDataSource(action) {
      if (action.type === 'GRAPHQL') {
        return this.loadGraphData(action);
      }

      if (action.type === 'REST') {
        return this.loadRestData(action);
      }

      throw new Error('Unknown action type');
    },

    async loadRestData(action) {
      return new Promise((resolve, reject) => {
        this.loadingQueries++;
        let url = action.url;

        if (action.params?.length) {
          if (action.paramsType === 'path') {
            url = action.params.reduce(
              (acc, name) => `${acc}/${this.$route.params.rowData[name]}`,
              url,
            );
          }
        }

        XHR.query(action.method, url, {
          absoluteUrl: true,
        }).then(
          (data) => {
            data = parseJson(data);

            data = {
              noPagination: true,
              totalCount: data.length,
              totalPages: 1,
              hasMore: false,
              rows: data.map((itemData) => ({
                id: itemData.id ?? uniqueId('vid'),
                data: itemData,
              })),
            };

            this.loadingQueries--;
            resolve(data);
          },
          (error) => {
            this.$notification.error({
              message: error.message,
            });

            this.loadingQueries--;
            reject();
          },
        );
      });
    },

    async loadGraphData(action) {
      let { data } = await this.$apollo.query({
        ...composeGqlQueryFromActionWithVariables({
          action,
          data: {},
          context: this,
          fieldsMeta: this.fieldsMeta,
        }),
        client: 'newApiClient',
        fetchPolicy: 'network-only',
      });

      data = action.path
        .split('.')
        .slice(1)
        .reduce((temp, key) => temp[key], data);

      return {
        totalCount: data.totalCount,
        totalPages: Math.ceil(data.totalCount / this.config.pageSize),
        hasMore: data.hasMore,
        rows: data.data.map((itemData) => ({ id: itemData.id, data: itemData })),
      };
    },

    getGraphFilterItem(field, operator, value, index) {
      const { renderer } = this.componentMeta.fields.find(({ name }) => name === field);

      if (renderer === 'refs') value = [value.value];
      if (renderer === 'ref') value = value.value;
      if (['date', 'date-time'].includes(renderer)) value = Number(value);

      return {
        field,
        operator: operator[index],
        value,
      };
    },

    async resetConfig() {
      let configData = deepClone(this.configData);
      if (configData) {
        configData = {
          ...configData,
          config: await prepareConfig(
            configData.config,
            this.fieldsMeta,
            CONFIG_LAYOUT,
            this.entityType,
            this.tableConfig,
          ),
        };
      }

      // Has table columns config
      if (this.tableConfig?.columns) {
        Object.entries(this.tableConfig?.columns).forEach(([name, params]) => {
          if (!params.hidden && !configData.config.displayFields.find((f) => f.name === name)) {
            // Add custom columns if not already exist in saved local config
            configData.config.displayFields.push({
              name,
              hidden: false,
            });
          } else if (params.hidden) {
            // Remove columns hidden by tables config
            const ind = configData.config.displayFields.findIndex((f) => f.name === name);
            if (ind > -1) configData.config.displayFields.splice(ind, 1);
          }
        });

        // If no local config exist presort columns by tables config
        if (!this.localStoredTableConfig) {
          const first = [];
          const mid = [];
          const last = [];
          configData.config.displayFields.forEach((item) => {
            const position = this.tableConfig.columns[item.name]?.position;
            if (position === 'left') first.push(item);
            else if (position === 'right') last.push(item);
            else mid.push(item);
          });

          configData.config.displayFields = first.concat(mid).concat(last);
        }
      }

      this.cachedConfig = configData;
    },

    routeQueryUpdate(queryParamsToAdd, paramsFilter = () => true) {
      const queryParamsToKeep = Object.entries(this.$route.query).filter(paramsFilter);
      if (this.isUseConfig) {
        this.$router
          .replace({
            query: {
              ...Object.fromEntries(queryParamsToKeep),
              ...queryParamsToAdd,
            },
          })
          .catch(() => {});
      }

      this.loadData();
    },

    updatePageQueryParams() {
      this.routeQueryUpdate(
        {
          page: this.page,
          pageSize: this.cachedConfig.config.pageSize,
        },
        ([key]) => !['page', 'pageSize'].includes(key),
      );
    },

    refetchTable(type) {
      if (!type || type === this.entityType) {
        this.requestError = null;
        this.$apollo.queries.data.refresh();
      }
    },

    getBaseConfig() {
      const storedConfig = this.localStoredTableConfig || {};
      return {
        title: `pageblock_${this.entityType}`,
        config: {
          ...storedConfig,
          pageSize: this.queryPageSize || storedConfig.pageSize,
        },
      };
    },

    getInitialSortParams() {
      let sortParams = [];
      if (this.isUseConfig) {
        const queryParams = this.isTable && this.$route.query;
        sortParams = (this.isTable && storage.get(`tableSorting_${this.pageUrl}`)) || [];

        if (queryParams.sort) {
          sortParams = [
            {
              field: queryParams.sort,
              direction: (queryParams.sortDir || '').toUpperCase(),
            },
          ];
        }
      } else if (this.setupSortParams) {
        sortParams = [
          {
            field: this.setupSortParams.sort,
            direction: (this.setupSortParams.sortDir || '').toUpperCase(),
          },
        ];
      }
      return sortParams;
    },

    parseFilterParamValue(key, value, isArray = true) {
      const fieldExist = this.fieldsMeta.find((field) => field.name === key.split('f_')[1]);
      if (!fieldExist) return null;

      if (isArray) {
        value = value.slice(1, -1).split(/;/);
        const list = value.map((v) => this.parseFilterParamValue(key, v, false));

        return {
          field: key.split('f_')[1],
          operator: list.map((i) => i.operator),
          value: list.map((i) => i.value),
        };
      }

      value = value.split(/\./);
      let operator = value.shift();
      if (operator === 'o') {
        operator = value.shift();
        value = {
          value: value.shift(),
          title: value.join('.'),
        };
      } else {
        value = value.join('.');
      }

      const { valueProcessor = (v) => v } =
        getFilterRendererByFormRenderer(
          this.fieldsMeta.find((f) => f.name === key.split('f_')[1]).renderer,
          { publishable: true },
        ) || {};

      return {
        field: key.split('f_')[1],
        operator,
        value: valueProcessor(value),
      };
    },

    getInitialFilterParams() {
      let filterParams = [];
      if (this.isUseConfig) {
        filterParams = storage.get(`tableFiltering_${this.pageUrl}`) || [];
      }
      // Если есть фильтры в адресной строке, берём их вместо сохранённых
      const queryFilterParams = Object.entries(
        this.isUseConfig ? this.$route.query : this.setupFilters,
      ).filter(([key]) => key.startsWith('f_'));

      if (queryFilterParams.length) {
        filterParams = queryFilterParams
          .map(([key, value]) => this.parseFilterParamValue(key, value))
          .filter((filterItem) => !!filterItem);
      } else {
        // Исключаем сохранённые фильтры по несуществующим полям
        filterParams = filterParams.filter((filterItem) =>
          this.fieldsMeta.find((field) => field.name === filterItem.field),
        );
      }

      return filterParams;
    },

    setSortParams(params) {
      if (this.isUseConfig) {
        storage.set(`tableSorting_${this.pageUrl}`, params);
      }
      this.sortParams = params;
      const queryParamsToAdd = {
        sort: params[0]?.field,
        sortDir: params[0]?.direction.toLowerCase(),
      };

      this.routeQueryUpdate(queryParamsToAdd);
    },

    getQueryParamForFilterItem({ field, operator, value }) {
      const list = value.map((v, index) => {
        if (typeof v === 'object') {
          v = `${v.value}.${v.title}`;
          return `o.${operator[index]}.${v}`;
        }

        return `${operator[index]}.${v}`;
      });

      return [`f_${field}`, `[${list.join(';')}]`];
    },

    setFilterParams(params, initialApply = false) {
      if (this.isUseConfig) {
        storage.set(`tableFiltering_${this.pageUrl}`, params);
      }
      if (!initialApply) {
        this.filterParams = params;
        this.page = 1;
      }

      const queryParamsToAdd = params.map(this.getQueryParamForFilterItem);
      this.routeQueryUpdate(Object.fromEntries(queryParamsToAdd), ([key]) => !key.startsWith('f_'));
    },

    async entityClick(id) {
      let error = null;
      const isCreating = !id;

      if (!isCreating && !this.canReadModel) {
        if (!this.canReadModel)
          error = this.$t('entity.error.noReadRights', { name: this.entityType });
      }

      if (isCreating) {
        if (!this.canCreateModel)
          error = this.$t('entity.error.noCreateRights', { name: this.entityType });
        else id = id || `_temp_${+new Date()}`;
      }

      if (error) {
        return bus.$emit('error', {
          message: error,
          type: 'warning',
        });
      }

      this.$emit(
        'entityClick',
        this.$route.params.type,
        id,
        false,
        false,
        isCreating ? {} : this.data.rows.find((row) => row.id === id).data,
      );
    },

    async updateEntity(id, data, updatedData) {
      if (!this.canUpdateModel) return;

      const draft = store.mutate.getFormDraft(this.entityType, id);
      if (draft) Object.assign(draft.data, updatedData);

      await EntityService.update(this.fieldsMeta, {
        id,
        type: this.entityType,
        data,
      });
    },

    toggleMenu() {
      bus.$emit('toggleMenu');
    },

    updateRows(rows) {
      this.data.rows = rows;
    },

    saveConfig() {
      const storaged = {};
      const storagedKeys = ['displayFields', 'displayType', 'pageSize'];

      Object.entries(this.config).forEach(([key, value]) => {
        if (storagedKeys.includes(key)) {
          storaged[key] = value;
        }
      });

      Object.assign(this.configData.config, storaged);

      const defaultStorage = storage.get('viewConfigData') || {};
      storage.set('viewConfigData', {
        ...defaultStorage,
        [this.pageUrl]: storaged,
      });
    },

    updatePageSize() {
      this.page = 1;
      this.saveConfig();
      this.updatePageQueryParams();
    },

    editModel() {
      this.$router.push({
        name: 'DataPageModel',
        params: {
          type: this.entityType,
        },
      });
    },
  },
};
</script>
